import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calculator, Bot, BarChart3, Cpu, Zap, ArrowUpRight,
  Sparkles, FileText, MessageCircle, Brain
} from 'lucide-react';

/**
 * IA Solutions Hub — `/ia-soluciones`
 * Centro neurálgico de IA de ALLCOM.
 * Paleta estricta monocromática (negro / blanco / grises).
 * No usa Teal/Cyan/Yellow del resto del sitio.
 */

const PILLARS = [
  {
    id: 'roi',
    icon: Calculator,
    title: 'Calculadora de ROI',
    desc: 'Mide el retorno real de tu operación con IA. Reporte ejecutivo en PDF en menos de 2 minutos.',
    href: '/ia/calculador-de-roi',
    cta: 'Calcular ROI',
    tag: 'Herramienta',
  },
  {
    id: 'openclaw',
    icon: Bot,
    title: 'OpenClaw · Agentes Autónomos',
    desc: 'Agentes IA que trabajan 24/7 sobre tu data lake. Implementador oficial en Chile.',
    href: '/ia/openclaw',
    cta: 'Conocer OpenClaw',
    tag: 'Plataforma',
  },
  {
    id: 'agentes',
    icon: Brain,
    title: 'Agentes IA a medida',
    desc: 'Agentes modulares para operación de sucursales. Sin orquestadores complejos.',
    href: '/ia/agentes-ia',
    cta: 'Diseñar agente',
    tag: 'Servicio',
  },
  {
    id: 'dashboards',
    icon: BarChart3,
    title: 'Dashboards Ejecutivos',
    desc: 'KPIs en tiempo real con predicciones automáticas para CEO, COO y C-Level.',
    href: '/ia/dashboards-ejecutivos',
    cta: 'Ver demo',
    tag: 'Producto',
  },
  {
    id: 'studio',
    icon: Sparkles,
    title: 'IA Studio',
    desc: 'Prueba conceptual en 48h, mockup en 4 días, implementación en 30. Consultoría IA aplicada.',
    href: '/ia/allcom-ia',
    cta: 'Ir al Studio',
    tag: 'Consultoría',
  },
  {
    id: 'kian',
    icon: MessageCircle,
    title: 'Kian™ Daily',
    desc: 'Briefing diario de IA para empresas. 6 noticias curadas por nuestro Chief AI Officer.',
    href: '/empresa/noticias',
    cta: 'Suscribirse',
    tag: 'Newsletter',
  },
];

const CAPABILITIES = [
  { label: 'Detección de fraude por imagen', value: '+99%', desc: 'Precisión en self-checkout' },
  { label: 'Reducción de filas', value: '−55%', desc: 'En operaciones de retail' },
  { label: 'Tiempo de implementación', value: '30d', desc: 'De idea a producción' },
  { label: 'Operación', value: '24/7', desc: 'Agentes siempre activos' },
];

const STEPS = [
  { n: '01', title: 'Diagnóstico', desc: 'Mapeamos tus procesos, datos y puntos de fricción.' },
  { n: '02', title: 'Prueba conceptual', desc: 'En 48 horas validamos hipótesis con un MVP funcional.' },
  { n: '03', title: 'Implementación', desc: 'Desplegamos agentes, dashboards y automatizaciones.' },
  { n: '04', title: 'Operación', desc: 'Monitoreo continuo, mejora del modelo y ROI medible.' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.21, 0.61, 0.35, 1] } },
};

export default function IASolutionsHub() {
  return (
    <div className="bg-white text-black" data-testid="ia-hub-page">
      {/* HERO — minimalista B&W */}
      <section className="border-b border-black/10" data-testid="ia-hub-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-xs font-mono uppercase tracking-[0.32em] text-black/50 mb-8"
            data-testid="ia-hub-eyebrow"
          >
            IA Hub · Centro Neurálgico
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.95] mb-10 max-w-4xl"
            style={{ fontFamily: 'Syne, sans-serif' }}
            data-testid="ia-hub-title"
          >
            Inteligencia Artificial<br />
            <span className="text-black/40">aplicada a tu negocio.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg text-black/70 leading-relaxed max-w-2xl mb-12"
          >
            Calculadora de ROI, agentes autónomos, dashboards ejecutivos y consultoría IA — todo desde un solo lugar. ALLCOM convierte modelos en operación real.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-3"
          >
            <Link
              to="/ia/calculador-de-roi"
              className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm font-semibold rounded-full hover:bg-black/80 transition-colors"
              data-testid="ia-hub-cta-roi"
            >
              Calcular ROI <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-6 py-3 border border-black text-black text-sm font-semibold rounded-full hover:bg-black hover:text-white transition-colors"
              data-testid="ia-hub-cta-contact"
            >
              Hablar con un especialista
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES STRIP */}
      <section className="border-b border-black/10" data-testid="ia-hub-capabilities">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {CAPABILITIES.map((c, i) => (
            <motion.div
              key={c.label}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              data-testid={`ia-hub-capability-${i}`}
            >
              <div className="text-3xl sm:text-4xl font-semibold mb-2 tabular-nums" style={{ fontFamily: 'Syne, sans-serif' }}>
                {c.value}
              </div>
              <div className="text-sm font-medium text-black mb-1">{c.label}</div>
              <div className="text-xs text-black/50">{c.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PILLARS GRID */}
      <section className="border-b border-black/10" data-testid="ia-hub-pillars">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="mb-16 max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.32em] text-black/50 mb-4">Pilares</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Seis maneras de aplicar IA hoy.
            </h2>
            <p className="text-base text-black/60 leading-relaxed">
              Cada pilar resuelve un problema concreto. Empieza por el que más impacto te genere.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10" data-testid="ia-hub-pillars-grid">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.id}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white p-8 sm:p-10 group hover:bg-black hover:text-white transition-colors duration-300"
                  data-testid={`ia-hub-pillar-${p.id}`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="w-7 h-7" strokeWidth={1.4} />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-black/40 group-hover:text-white/40">
                      {p.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 leading-snug" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {p.title}
                  </h3>
                  <p className="text-sm text-black/60 group-hover:text-white/70 leading-relaxed mb-6 transition-colors">
                    {p.desc}
                  </p>
                  <Link
                    to={p.href}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold border-b border-current pb-0.5 hover:gap-2.5 transition-all"
                    data-testid={`ia-hub-pillar-cta-${p.id}`}
                  >
                    {p.cta} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS / METHOD */}
      <section className="border-b border-black/10" data-testid="ia-hub-process">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="mb-16 max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.32em] text-black/50 mb-4">Método</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              De la idea a producción en 30 días.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="border-t border-black pt-6"
                data-testid={`ia-hub-step-${s.n}`}
              >
                <div className="text-xs font-mono text-black/40 mb-4">{s.n}</div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-black/60 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-black text-white" data-testid="ia-hub-final-cta">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
          <Zap className="w-8 h-8 mx-auto mb-8 opacity-50" strokeWidth={1.2} />
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 max-w-3xl mx-auto leading-[1.05]" style={{ fontFamily: 'Syne, sans-serif' }}>
            ¿Listo para mover tu negocio con IA?
          </h2>
          <p className="text-base sm:text-lg text-white/60 max-w-xl mx-auto mb-12 leading-relaxed">
            Conversemos. En 30 minutos te mostramos cómo aplicar IA en tu operación, con un caso real comparable al tuyo.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black text-sm font-semibold rounded-full hover:bg-white/90 transition-colors"
              data-testid="ia-hub-cta-meeting"
            >
              Agendar 30 min <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/ia/calculador-de-roi"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white text-sm font-semibold rounded-full hover:bg-white/10 transition-colors"
              data-testid="ia-hub-cta-roi-secondary"
            >
              Calcular ROI primero
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
